import { useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Users,
  CheckSquare,
} from 'lucide-react'
import type { Interview, Facet } from '@/types/interviewer'

interface StakeholderMapProps {
  interviews: Interview[]
  onSelectInterview?: (interview: Interview) => void
}

interface StakeholderEntry {
  name: string
  mentions: number
  decisions: Array<{
    facet: Facet
    interview: Interview
  }>
  roles: Set<string>
}

export function StakeholderMap({
  interviews,
  onSelectInterview,
}: StakeholderMapProps) {
  // Extract and aggregate stakeholders
  const stakeholders = useMemo(() => {
    const map = new Map<string, StakeholderEntry>()

    for (const interview of interviews) {
      if (interview.mode !== 'decisions' || interview.status !== 'analyzed') continue

      const stakeholderFacets = interview.facets.filter(
        f => f.type === 'stakeholder' && !f.dismissed
      )
      const decisionFacets = interview.facets.filter(
        f => f.type === 'decision' && !f.dismissed
      )

      for (const facet of stakeholderFacets) {
        // Normalize stakeholder name
        const name = facet.content.trim()
        const key = name.toLowerCase()

        if (!map.has(key)) {
          map.set(key, {
            name,
            mentions: 0,
            decisions: [],
            roles: new Set(),
          })
        }

        const entry = map.get(key)!
        entry.mentions++

        // Link to decisions in the same interview
        for (const decision of decisionFacets) {
          entry.decisions.push({ facet: decision, interview })
        }

        // Extract role if mentioned
        const roleMatch = facet.evidence.match(/\b(PM|engineer|designer|CEO|CTO|VP|manager|lead|director)\b/i)
        if (roleMatch) {
          entry.roles.add(roleMatch[1].toUpperCase())
        }
      }
    }

    return [...map.values()].sort((a, b) => b.mentions - a.mentions)
  }, [interviews])

  if (stakeholders.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Users className="w-10 h-10 text-text-muted mx-auto mb-3" />
        <h3 className="font-medium mb-1">No stakeholders identified</h3>
        <p className="text-sm text-text-muted">
          Stakeholders are extracted from decision interviews
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Stakeholder Map</h3>
        <span className="text-xs text-text-muted">
          {stakeholders.length} stakeholder{stakeholders.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {stakeholders.map((stakeholder) => (
          <StakeholderCard
            key={stakeholder.name}
            stakeholder={stakeholder}
            onSelectDecision={(interview) => onSelectInterview?.(interview)}
          />
        ))}
      </div>
    </div>
  )
}

function StakeholderCard({
  stakeholder,
  onSelectDecision,
}: {
  stakeholder: StakeholderEntry
  onSelectDecision: (interview: Interview) => void
}) {
  const uniqueDecisions = useMemo(() => {
    const seen = new Set<string>()
    return stakeholder.decisions.filter(d => {
      if (seen.has(d.facet.id)) return false
      seen.add(d.facet.id)
      return true
    })
  }, [stakeholder.decisions])

  return (
    <Card className="p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-business/10 flex items-center justify-center flex-shrink-0">
          <Users className="w-5 h-5 text-business" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium truncate">{stakeholder.name}</h4>
          <div className="flex items-center gap-2 mt-1">
            {[...stakeholder.roles].map((role) => (
              <Badge key={role} variant="secondary" className="text-xs">
                {role}
              </Badge>
            ))}
            <span className="text-xs text-text-muted">
              {stakeholder.mentions} mention{stakeholder.mentions > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Decisions involved in */}
      {uniqueDecisions.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs text-text-muted">
            <CheckSquare className="w-3 h-3" />
            <span>Involved in {uniqueDecisions.length} decision{uniqueDecisions.length > 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-1">
            {uniqueDecisions.slice(0, 3).map(({ facet, interview }) => (
              <button
                key={facet.id}
                onClick={() => onSelectDecision(interview)}
                className="w-full text-left p-2 rounded bg-warm-50 hover:bg-warm-100 transition-colors"
              >
                <p className="text-xs line-clamp-1">{facet.content}</p>
                <span className="text-xs text-text-muted">{interview.title}</span>
              </button>
            ))}
            {uniqueDecisions.length > 3 && (
              <p className="text-xs text-text-muted pl-2">
                +{uniqueDecisions.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}
